import { IGatsbyImageData } from 'gatsby-plugin-image'
import React, { FC, useCallback, useMemo, useRef, useState } from 'react'
import BertholdTitle from 'src/components/atoms/bertholdTitle'
import { SegmentIndicator } from 'src/components/atoms/segmentIndicator'
import { StoryCarouselCard } from 'src/components/molecules/cards/storyCarouselCard'
import {
  DraggableSlider,
  DraggableSliderHandle,
} from 'src/components/molecules/draggableSlider'
import Eyebrow from 'src/images/svgs/Eyebrow.svg'
import BackwardCarrotIcon from 'src/images/svgs/icons/BackwardCarrotIcon.svg'
import ForwardCarrotIcon from 'src/images/svgs/icons/ForwardCarrotIcon.svg'
import ArrowLeft from 'src/images/svgs/icons/ArrowLeft.svg'
import ArrowRight from 'src/images/svgs/icons/ArrowRight.svg'
import { PrismicStructuredTextType } from 'src/typings/generated/graphql'
import { generateRandomId } from 'src/utils/domHelper'
import { useMediaQuery } from 'src/utils/useMediaQuery'
import { useTranslation } from 'src/utils/useTranslation'
import * as Styles from './storyCarousel.module.scss'

/**
 * Type representing a single card entry in the `StoryCarousel`.
 */
export type StoryCarouselCardEntry = {
  title: string
  body: PrismicStructuredTextType
  image: IGatsbyImageData
  imageAlt?: string
}

/**
 * Type representing a year entry in the `StoryCarousel`, which contains a year and an array of cards for that year.
 */
export type StoryCarouselYearEntry = {
  year: string
  cardEntries: StoryCarouselCardEntry[]
}

/**
 * Props for the `StoryCarousel` component.
 */
export type StoryCarouselProps = {
  anchor?: string
  headline?: string
  yearEntries?: StoryCarouselYearEntry[]
}

const StoryCarousel: FC<StoryCarouselProps> = ({
  anchor = generateRandomId(),
  headline = '',
  yearEntries = [],
}) => {
  const isWideViewport = useMediaQuery('(min-width: 62em)')
  const { t } = useTranslation()

  const yearViewSliderRef = useRef<DraggableSliderHandle>(null)
  const cardViewSliderRef = useRef<DraggableSliderHandle>(null)

  const [currentYear, setCurrentYear] = useState<string | undefined>(
    yearEntries.length > 0 ? yearEntries[0].year : undefined
  )
  const [currentCardIndex, setCurrentCardIndex] = useState(0)

  // Card view mode: maps year → starting flat card index
  const firstCardIndexByYear = useMemo(() => {
    const { indices } = yearEntries.reduce(
      (acc, yearEntry) => ({
        indices: { ...acc.indices, [yearEntry.year]: acc.runningIndex },
        runningIndex: acc.runningIndex + yearEntry.cardEntries.length,
      }),
      { indices: {} as Record<string, number>, runningIndex: 0 }
    )
    return indices
  }, [yearEntries])

  // Card view mode: maps flat card index → year
  const yearForCardIndex = useMemo(() => {
    return yearEntries.flatMap(yearEntry =>
      yearEntry.cardEntries.map(() => yearEntry.year)
    )
  }, [yearEntries])

  const slideCount = useMemo(() => {
    return isWideViewport
      ? yearEntries.length
      : yearEntries.flatMap(e => e.cardEntries).length
  }, [isWideViewport, yearEntries])

  const currentSlideIndex = useMemo(() => {
    return isWideViewport
      ? yearEntries.findIndex(e => e.year === currentYear)
      : firstCardIndexByYear[currentYear ?? ''] + currentCardIndex
  }, [
    isWideViewport,
    yearEntries,
    currentYear,
    firstCardIndexByYear,
    currentCardIndex,
  ])

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const isFirstSlide = useMemo(
    () => currentSlideIndex === 0,
    [currentSlideIndex]
  )

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const isLastSlide = useMemo(
    () => currentSlideIndex === slideCount - 1,
    [currentSlideIndex, slideCount]
  )

  // Year view mode: index = year index
  const handleYearViewSlideChange = useCallback(
    (newIndex: number) => {
      const year = yearEntries[newIndex]?.year
      if (year) {
        setCurrentYear(year)
        setCurrentCardIndex(0)
      }
    },
    [yearEntries]
  )

  // Card view mode: index = flat card index
  const handleCardViewSlideChange = useCallback(
    (newIndex: number) => {
      const year = yearForCardIndex[newIndex]
      if (year) {
        setCurrentYear(year)

        // Calculate the index of the card within its year by subtracting the starting index of that year
        const yearStart = firstCardIndexByYear[year] ?? 0
        setCurrentCardIndex(newIndex - yearStart)
      }
    },
    [yearForCardIndex, firstCardIndexByYear]
  )

  const onYearSelect = useCallback(
    (year: string) => {
      if (isWideViewport) {
        const yearIndex = yearEntries.findIndex(e => e.year === year)
        yearViewSliderRef.current?.goTo(yearIndex)
      } else {
        const cardIndex = firstCardIndexByYear[year] ?? 0
        cardViewSliderRef.current?.goTo(cardIndex)
      }

      setCurrentYear(year)
      setCurrentCardIndex(0)
    },
    [isWideViewport, yearEntries, firstCardIndexByYear]
  )

  // Compute initial slide indices from shared state so that when a slider
  // mounts (including after a viewport transition) it starts at the right position.
  const yearViewSliderStartIndex = Math.max(
    0,
    yearEntries.findIndex(e => e.year === currentYear)
  )
  const cardViewSliderStartIndex = Math.max(
    0,
    (firstCardIndexByYear[currentYear ?? ''] ?? 0) + currentCardIndex
  )

  const yearPills = useMemo(() => {
    return (
      <div className={Styles.yearPillList}>
        {yearEntries.map((yearEntry, index) => {
          const key = `${index}-${yearEntry.year}`
          const className = `${Styles.yearPill} ${
            yearEntry.year === currentYear ? Styles.activeYearPill : ''
          }`

          return (
            <button
              type="button"
              key={key}
              className={className}
              onClick={() => onYearSelect(yearEntry.year)}
            >
              {yearEntry.year}
            </button>
          )
        })}
      </div>
    )
  }, [yearEntries, currentYear, onYearSelect])

  const renderCard = useCallback(
    (cardEntry: StoryCarouselCardEntry, key: string) => (
      <StoryCarouselCard
        key={key}
        className={Styles.card}
        image={cardEntry.image}
        imageAlt={cardEntry.imageAlt ?? ''}
        title={cardEntry.title}
        body={cardEntry.body}
      />
    ),
    []
  )

  const renderChevronButton = useCallback(
    (
      direction: 'backward' | 'forward',
      ariaLabel: string,
      onClick: () => void
    ) => {
      return (
        <button
          type="button"
          className={Styles.chevronButton}
          onClick={onClick}
          aria-label={ariaLabel}
        >
          {direction === 'backward' ? (
            <BackwardCarrotIcon />
          ) : (
            <ForwardCarrotIcon />
          )}
        </button>
      )
    },
    []
  )

  const renderArrowButton = useCallback(
    (
      direction: 'backward' | 'forward',
      ariaLabel: string,
      onClick: () => void
    ) => {
      return (
        <button
          type="button"
          className={Styles.arrowButton}
          onClick={onClick}
          aria-label={ariaLabel}
        >
          {direction === 'backward' ? <ArrowLeft /> : <ArrowRight />}
        </button>
      )
    },
    []
  )

  const yearSlides = useMemo(
    () =>
      yearEntries.map(yearEntry => (
        <div key={yearEntry.year} className={Styles.yearSlide}>
          {yearEntry.cardEntries.map((cardEntry, cardIndex) =>
            renderCard(cardEntry, `${yearEntry.year}-${cardIndex}`)
          )}
        </div>
      )),
    [yearEntries, renderCard]
  )

  const cardSlides = useMemo(
    () =>
      yearEntries.flatMap(yearEntry =>
        yearEntry.cardEntries.map((cardEntry, cardIndex) =>
          renderCard(cardEntry, `${yearEntry.year}-${cardIndex}`)
        )
      ),
    [yearEntries, renderCard]
  )

  const yearViewSlider = useMemo(
    () => (
      <div className={Styles.yearViewSliderLayout} key="year-slider-layout">
        <div className={Styles.yearViewPreviousArrowButtonContainer}>
          {!isFirstSlide &&
            renderChevronButton(
              'backward',
              t('components.StoryCarousel.previousSlideButtonAriaLabel'),
              () => {
                yearViewSliderRef.current?.goTo(currentSlideIndex - 1)
              }
            )}
        </div>

        <DraggableSlider
          key="year-slider"
          ref={yearViewSliderRef}
          ariaLabel={headline}
          startIndex={yearViewSliderStartIndex}
          onSlideChange={handleYearViewSlideChange}
        >
          {yearSlides}
        </DraggableSlider>

        <div className={Styles.yearViewNextArrowButtonContainer}>
          {!isLastSlide &&
            renderChevronButton(
              'forward',
              t('components.StoryCarousel.nextSlideButtonAriaLabel'),
              () => {
                yearViewSliderRef.current?.goTo(currentSlideIndex + 1)
              }
            )}
        </div>
      </div>
    ),
    [
      headline,
      yearViewSliderStartIndex,
      handleYearViewSlideChange,
      yearSlides,
      isFirstSlide,
      isLastSlide,
      currentSlideIndex,
      renderChevronButton,
      t,
    ]
  )

  const cardViewSlider = useMemo(
    () => (
      <div className={Styles.cardViewSliderLayout} key="card-slider-layout">
        <div className={Styles.cardViewPreviousArrowButtonContainer}>
          {!isFirstSlide &&
            renderArrowButton(
              'backward',
              t('components.StoryCarousel.previousSlideButtonAriaLabel'),
              () => {
                cardViewSliderRef.current?.goTo(currentSlideIndex - 1)
              }
            )}
        </div>

        <DraggableSlider
          key="card-slider"
          ref={cardViewSliderRef}
          ariaLabel={headline}
          startIndex={cardViewSliderStartIndex}
          onSlideChange={handleCardViewSlideChange}
        >
          {cardSlides}
        </DraggableSlider>

        <div className={Styles.cardViewNextArrowButtonContainer}>
          {!isLastSlide &&
            renderArrowButton(
              'forward',
              t('components.StoryCarousel.nextSlideButtonAriaLabel'),
              () => {
                cardViewSliderRef.current?.goTo(currentSlideIndex + 1)
              }
            )}
        </div>
      </div>
    ),
    [
      headline,
      cardViewSliderStartIndex,
      handleCardViewSlideChange,
      cardSlides,
      isFirstSlide,
      isLastSlide,
      currentSlideIndex,
      renderArrowButton,
      t,
    ]
  )

  const shouldRenderContent = useMemo(() => {
    return headline && yearEntries.length > 0
  }, [headline, yearEntries])

  return (
    <section id={anchor} className={Styles.storyCarousel}>
      {shouldRenderContent && (
        <div className={Styles.content}>
          {headline && (
            <div className={Styles.headlineContainer}>
              <Eyebrow className={Styles.eyebrow} />
              <h2 className={Styles.headline}>
                {BertholdTitle({ text: headline })}
              </h2>

              {isWideViewport && yearPills}
            </div>
          )}

          <div className={Styles.sliderContainer}>
            {!isWideViewport && yearPills}

            {isWideViewport ? yearViewSlider : cardViewSlider}
          </div>

          <div className={Styles.segmentIndicatorContainer}>
            <SegmentIndicator
              segmentCount={slideCount}
              currentIndex={currentSlideIndex}
            />
          </div>
        </div>
      )}
    </section>
  )
}

export default StoryCarousel
